import { useEffect, useState, type FC } from 'react';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Database,
  Layers,
  RefreshCw,
  Server,
} from 'lucide-react';
import { apiClient, ApiClientError } from '../services/api-client';
import type { HealthCheckResponse } from '@antigravity/types';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export const HealthPage: FC = () => {
  const [health, setHealth] = useState<HealthCheckResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getHealth();
      setHealth(data);
      setLastChecked(new Date());
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(`Failed to query API: ${err.message} (HTTP ${err.statusCode})`);
      } else {
        setError((err as Error).message || 'Unable to connect to Antigravity API server.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-extrabold text-white flex items-center gap-2.5">
            <Activity className="w-6 h-6 text-brand-400" />
            Cluster Diagnostics & Health
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Real-time status of keerav camp API Gateway, PostgreSQL Database, and Redis Cache.
          </p>
        </div>

        <button
          onClick={fetchHealth}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 border border-slate-700 hover:bg-slate-800 text-white text-xs font-semibold transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh Status</span>
        </button>
      </div>

      {loading && !health && <LoadingSpinner label="Querying cluster health status..." />}

      {error && (
        <div className="p-5 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-start gap-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-red-300">Connection Failed</p>
            <p className="text-xs text-red-400/90">{error}</p>
            <p className="text-[11px] text-slate-400 mt-2">
              Ensure the backend API server is started via <code className="bg-slate-900 px-1 py-0.5 rounded text-amber-300">npm run dev:api</code> on port 4000.
            </p>
          </div>
        </div>
      )}

      {health && (
        <div className="space-y-6">
          {/* Top Status Card */}
          <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${
                  health.status === 'healthy'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                }`}
              >
                {health.status === 'healthy' ? (
                  <CheckCircle2 className="w-6 h-6" />
                ) : (
                  <AlertCircle className="w-6 h-6" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-white capitalize">{health.status}</h2>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">
                    {health.service} v{health.version}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Uptime: {health.uptimeSeconds}s | Environment: {health.environment}
                </p>
              </div>
            </div>

            {lastChecked && (
              <div className="text-xs text-slate-400">
                Last checked: <span className="font-mono text-slate-300">{lastChecked.toLocaleTimeString()}</span>
              </div>
            )}
          </div>

          {/* Dependency Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Database Card */}
            <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <Database className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-sm font-bold text-white">PostgreSQL Database</h3>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    health.dependencies.database.status === 'healthy'
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}
                >
                  {health.dependencies.database.status}
                </span>
              </div>
              <div className="space-y-2 text-xs text-slate-400">
                <div className="flex justify-between py-1 border-b border-slate-800/40">
                  <span>Latency</span>
                  <span className="font-mono text-slate-200">
                    {health.dependencies.database.latencyMs !== undefined
                      ? `${health.dependencies.database.latencyMs}ms`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span>Message</span>
                  <span className="text-slate-300 text-right truncate max-w-[200px]">
                    {health.dependencies.database.message || 'Connected'}
                  </span>
                </div>
              </div>
            </div>

            {/* Redis Card */}
            <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <Layers className="w-5 h-5 text-red-400" />
                  <h3 className="text-sm font-bold text-white">Redis Queue / Cache</h3>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    health.dependencies.redis.status === 'healthy'
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}
                >
                  {health.dependencies.redis.status}
                </span>
              </div>
              <div className="space-y-2 text-xs text-slate-400">
                <div className="flex justify-between py-1 border-b border-slate-800/40">
                  <span>Latency</span>
                  <span className="font-mono text-slate-200">
                    {health.dependencies.redis.latencyMs !== undefined
                      ? `${health.dependencies.redis.latencyMs}ms`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span>Message</span>
                  <span className="text-slate-300 text-right truncate max-w-[200px]">
                    {health.dependencies.redis.message || 'Connected'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Memory Telemetry */}
          <div className="p-5 rounded-2xl bg-slate-900/30 border border-slate-800 text-xs">
            <h4 className="text-slate-300 font-semibold mb-3 flex items-center gap-2">
              <Server className="w-4 h-4 text-blue-400" />
              <span>Process Memory Consumption (MB)</span>
            </h4>
            <div className="grid grid-cols-3 gap-4 font-mono text-slate-300">
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800/80">
                <p className="text-[10px] text-slate-400 uppercase font-sans">Heap Used</p>
                <p className="text-base font-bold text-white mt-1">
                  {health.memoryUsageMb.heapUsed} MB
                </p>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800/80">
                <p className="text-[10px] text-slate-400 uppercase font-sans">Heap Total</p>
                <p className="text-base font-bold text-white mt-1">
                  {health.memoryUsageMb.heapTotal} MB
                </p>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800/80">
                <p className="text-[10px] text-slate-400 uppercase font-sans">RSS</p>
                <p className="text-base font-bold text-white mt-1">
                  {health.memoryUsageMb.rss} MB
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
